import { useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Badge } from "@shared/ui/badge";
import { ScrollArea } from "@shared/ui/scroll-area";
import { ContextualHelp } from "@shared/ui/contextual-help";
import {
  Calendar,
  Clock,
  Settings,
  Users,
  AlertTriangle,
  CheckCircle,
  Info,
  Tag,
  MessageSquare,
  Timer,
  Shield,
} from "lucide-react";
import { ViewFieldProps } from "@features/cx/settings/types";
import { ComplaintTypeViewFormProps } from "@features/cx/settings/types";

// Auto-populated View Component
function ViewField({
  label,
  value,
  type = "text",
  icon,
  className = "col-span-1",
  badgeVariant = "default",
}: ViewFieldProps) {
  const renderValue = () => {
    if (value === null || value === undefined || value === "") {
      return <span className="text-muted-foreground">Not specified</span>;
    }

    switch (type) {
      case "badge":
        return <Badge variant={badgeVariant}>{value}</Badge>;

      case "boolean":
        return (
          <div className="flex items-center gap-2">
            {value ? (
              <>
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-green-700">Yes</span>
              </>
            ) : (
              <>
                <AlertTriangle className="h-4 w-4 text-gray-400" />
                <span className="text-gray-600">No</span>
              </>
            )}
          </div>
        );

      case "time":
        return (
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4 text-blue-600" />
            <span>{value} hours</span>
          </div>
        );

      case "array":
        if (Array.isArray(value)) {
          return (
            <div className="flex flex-wrap gap-1">
              {value.map((item, index) => (
                <Badge key={index} variant="outline" className="text-xs">
                  {typeof item === "object" ? item.name || item.label : item}
                </Badge>
              ))}
            </div>
          );
        }
        return <span>{value}</span>;

      default:
        return <span className="break-words">{value}</span>;
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center gap-2">
        {icon}
        <label className="text-sm font-medium text-gray-700">{label}</label>
      </div>
      <div className="pl-6">{renderValue()}</div>
    </div>
  );
}

// Helper function to transform complaint data
function getComplaintData(complaintType: any) {
  if (!complaintType) {
    return null;
  }

  return {
    name: complaintType.name || "N/A",
    code: complaintType.configurationCode || complaintType.code || "N/A",
    description:
      complaintType.longDescription ||
      complaintType.description ||
      "No description available",
    status:
      complaintType.isActive !== undefined
        ? complaintType.isActive
          ? "Active"
          : "Inactive"
        : "Unknown",
    dateCreated: complaintType.createdDate,

    // Classification - with fallbacks
    utilityServices:
      complaintType.productCode || complaintType.utilityService || [],
    category:
      complaintType.supportRequestTypeDisplay ||
      complaintType.category ||
      "Not specified",
    subcategory:
      complaintType.supportRequestSubtypeDisplay ||
      complaintType.subcategory ||
      "Not specified",

    // Severity and Resolution Configuration - with fallbacks and null checks
    severity:
      complaintType.extraData.severityLevelDisplay ||
      complaintType.severity ||
      "Not specified",
    acknowledgmentTime:
      complaintType.extraData.acknowlegmentTime ||
      complaintType.acknowledgmentTime ||
      "Not specified",
    responseTime:
      complaintType.extraData.responseTime ||
      complaintType.responseTime ||
      "Not specified",
    resolutionTime:
      complaintType.extraData.resolutionTime ||
      complaintType.resolutionTime ||
      "Not specified",
    updateFrequency:
      complaintType.extraData.consumerUpdateFrequencyDisplay ||
      complaintType.updateFrequency ||
      "Not specified",

    customerConfirmation:
      complaintType.extraData.consumerConfirmationRequired !== undefined
        ? complaintType.extraData.consumerConfirmationRequired
        : complaintType.customerConfirmation || false,
    qualityReview:
      complaintType.extraData.qualityAssuranceReview !== undefined
        ? complaintType.extraData.qualityAssuranceReview
        : complaintType.qualityReview || false,
  };
}

export function ComplaintTypeViewForm({
  complaintType,
  open,
  onClose,
  onEdit,
}: ComplaintTypeViewFormProps) {
  if (!complaintType) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>No Complaint Data</DialogTitle>
          </DialogHeader>
          <div className="p-4">
            <p>No complaint type data available to display.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Helper functions for badge variants
  const getStatusBadgeVariant = (isActive: boolean) => {
    return isActive ? "default" : "secondary";
  };

  const getSeverityBadgeVariant = (severity: string) => {
    switch (severity?.toLowerCase()) {
      case "critical":
        return "destructive";
      case "high":
        return "default";
      case "medium":
      case "normal":
        return "secondary";
      case "low":
        return "outline";
      default:
        return "secondary";
    }
  };

  const complaintData = getComplaintData(complaintType);

  if (!complaintData) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center">
            <div className="flex items-center gap-3">
              <MessageSquare className="h-5 w-5 text-blue-600" />
              Complaint Type Details: {complaintData.name}
              <ContextualHelp
                title="Complaint Type Information"
                content="View comprehensive details about this complaint type configuration including classification, severity levels, resolution requirements, and closure procedures."
              />
            </div>
            <Badge variant={getStatusBadgeVariant(complaintType.isActive)}>
              {complaintData.status}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1 px-1">
          <div className="space-y-6 pr-4">
            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Info className="h-5 w-5" />
                  Basic Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ViewField
                    label="Complaint Type Name"
                    value={complaintData.name}
                    icon={<Tag className="h-4 w-4 text-blue-600" />}
                  />
                  <ViewField
                    label="Type Code"
                    value={complaintData.code}
                    icon={<Tag className="h-4 w-4 text-purple-600" />}
                  />
                  <ViewField
                    label="Description"
                    value={complaintData.description}
                    icon={<Info className="h-4 w-4 text-gray-600" />}
                  />
                  <ViewField
                    label="Date Created"
                    value={complaintData.dateCreated || "Not available"}
                    icon={<Calendar className="h-4 w-4 text-green-600" />}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Complaint Classification */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Users className="h-5 w-5" />
                  Complaint Classification
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ViewField
                    label="Utility Services"
                    value={complaintData.utilityServices}
                    type="array"
                    icon={<Settings className="h-4 w-4 text-blue-600" />}
                  />
                  <ViewField
                    label="Complaint Category"
                    value={complaintData.category}
                    icon={<Tag className="h-4 w-4 text-indigo-600" />}
                  />
                  <ViewField
                    label="Complaint Subcategory"
                    value={complaintData.subcategory}
                    icon={<Tag className="h-4 w-4 text-indigo-400" />}
                  />
                  <ViewField
                    label="Severity Level"
                    value={complaintData.severity}
                    type="badge"
                    badgeVariant={getSeverityBadgeVariant(
                      complaintData.severity
                    )}
                    icon={<Shield className="h-4 w-4 text-red-600" />}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Resolution Configuration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Timer className="h-5 w-5" />
                  Resolution Configuration
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ViewField
                    label="Acknowledgment Time"
                    value={complaintData.acknowledgmentTime}
                    icon={<Clock className="h-4 w-4 text-orange-600" />}
                  />
                  <ViewField
                    label="Response Time"
                    value={complaintData.responseTime}
                    icon={<Clock className="h-4 w-4 text-orange-600" />}
                  />
                  <ViewField
                    label="Resolution Time"
                    value={complaintData.resolutionTime}
                    icon={<Clock className="h-4 w-4 text-orange-600" />}
                  />
                  <ViewField
                    label="Customer Update Frequency"
                    value={complaintData.updateFrequency}
                    icon={<Clock className="h-4 w-4 text-purple-600" />}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Closure Requirements */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <CheckCircle className="h-5 w-5" />
                  Closure Requirements
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ViewField
                    label="Customer Confirmation Required"
                    value={complaintData.customerConfirmation}
                    type="boolean"
                  />
                  <ViewField
                    label="Quality Assurance Review"
                    value={complaintData.qualityReview}
                    type="boolean"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </ScrollArea>

        <DialogFooter className="flex-shrink-0 mt-4">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {onEdit && <Button onClick={onEdit}>Edit Complaint Type</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
